import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useNavigate } from "react-router-dom";
import { TOPIC } from "../constants/testIds";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { ChevronDown, ChevronRight } from "lucide-react";

const GRADES = [
  { v: "9", l: "9. Sınıf" }, { v: "10", l: "10. Sınıf" }, { v: "11", l: "11. Sınıf" },
  { v: "12", l: "12. Sınıf" }, { v: "GRADUATE", l: "Mezun" },
];

/**
 * 3 seviyeli müfredat: Üst Grup → Ana Konu → Alt Konu (accordion).
 * Sınav ayrımı korunur: LGS/TYT tek liste; YKS → shadcn Tabs [TYT][AYT].
 */
export default function TopicSelect() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [tree, setTree] = useState({ groups: [], ungrouped: [] });
  const [tab, setTab] = useState("TYT");
  const [openGroup, setOpenGroup] = useState(null);
  const [openTopic, setOpenTopic] = useState(null);
  const nav = useNavigate();

  const examType = profile?.exam_type || null;
  const needsOnboarding = !!profile && user?.role !== "admin" && !examType;

  useEffect(() => {
    (async () => {
      try { setProfile((await api.get("/me/profile")).data); }
      catch { setProfile({}); }
    })();
  }, []);

  useEffect(() => {
    if (!profile || needsOnboarding) return;
    const params = examType === "LGS" ? "?exam_level=LGS"
      : examType === "TYT" ? "?exam_level=TYT"
      : examType === "YKS" ? `?exam_level=${tab}` : "";
    (async () => {
      try { setTree((await api.get(`/catalog/curriculum${params}`)).data); }
      catch { setTree({ groups: [], ungrouped: [] }); }
    })();
    setOpenGroup(null); setOpenTopic(null);
  }, [profile, examType, tab, needsOnboarding]);

  const start = async (topic_id, subtopic_id = null) => {
    try {
      const { data } = await api.post("/sessions/start", { topic_id, subtopic_id });
      nav(`/oturum/${data.session_id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Oturum başlatılamadı"); }
  };

  const renderTopic = (t) => {
    const subs = (t.subtopics || []).filter((s) => s.is_active !== false);
    const isOpen = openTopic === t.topic_id;
    return (
      <div key={t.topic_id} data-testid={TOPIC.card(t.slug)}
           className="rounded-lg border border-[#E2E8F0] bg-white overflow-hidden">
        <button onClick={() => setOpenTopic(isOpen ? null : t.topic_id)}
                data-testid={`topic-toggle-${t.topic_id}`}
                className="w-full p-3 flex items-center justify-between hover:bg-slate-50 text-left">
          <div className="flex items-center gap-2 min-w-0">
            {isOpen ? <ChevronDown size={16} className="text-[#007BFF] shrink-0"/> : <ChevronRight size={16} className="text-slate-400 shrink-0"/>}
            <div>
              <div className="text-sm font-medium text-[#0B1A35]">{t.name}</div>
              <div className="text-xs text-slate-500">{subs.length} alt konu</div>
            </div>
          </div>
          <span className="text-xs text-[#007BFF] shrink-0">{isOpen ? "Kapat" : "Aç"}</span>
        </button>
        {isOpen && (
          <div className="border-t border-[#E2E8F0] p-3 flex flex-wrap gap-2">
            <button onClick={() => start(t.topic_id)} data-testid={TOPIC.startBtn(t.topic_id)}
                    className="yz-cta px-3 py-1.5 rounded-full text-sm">
              Tüm konudan 10 soru
            </button>
            {subs.map((s) => (
              <button key={s.subtopic_id} onClick={() => start(t.topic_id, s.subtopic_id)}
                      data-testid={`subtopic-start-${s.subtopic_id}`}
                      className="px-3 py-1.5 rounded-full border border-[#E2E8F0] text-sm hover:border-[#007BFF] hover:text-[#007BFF] transition-colors">
                {s.name}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderGroup = (g, idx) => {
    const isOpen = openGroup === g.group_id;
    const topics = (g.topics || []).filter((t) => t.is_active !== false);
    return (
      <div key={g.group_id} data-testid={`curriculum-group-${g.group_id}`}
           className="rounded-xl border border-[#E2E8F0] bg-white overflow-hidden">
        <button onClick={() => setOpenGroup(isOpen ? null : g.group_id)}
                data-testid={`group-toggle-${g.group_id}`}
                className="w-full p-4 flex items-center justify-between hover:bg-[#FFF8E1] text-left">
          <div className="flex items-center gap-3 min-w-0">
            {isOpen ? <ChevronDown size={18} className="text-[#007BFF] shrink-0"/> : <ChevronRight size={18} className="text-slate-400 shrink-0"/>}
            <div>
              <div className="text-xs text-slate-500 font-mono">{idx + 1}. Grup</div>
              <div className="font-display text-lg tracking-tight text-[#0B1A35]">{g.name}</div>
            </div>
          </div>
          <span className="text-xs text-slate-500 shrink-0">{topics.length} ana konu</span>
        </button>
        {isOpen && (
          <div className="border-t border-[#E2E8F0] p-3 space-y-2 bg-slate-50">
            {topics.map(renderTopic)}
            {topics.length === 0 && (
              <div className="text-sm text-slate-500 text-center py-3">Bu grupta henüz ana konu yok.</div>
            )}
          </div>
        )}
      </div>
    );
  };

  const groupList = (
    <div className="space-y-3">
      {tree.groups.filter((g) => g.is_active !== false).map(renderGroup)}
      {tree.ungrouped.filter((t) => t.is_active !== false).length > 0 && (
        <div className="rounded-xl border border-dashed border-[#E2E8F0] bg-white overflow-hidden"
             data-testid="curriculum-ungrouped">
          <button onClick={() => setOpenGroup(openGroup === "__ungrouped__" ? null : "__ungrouped__")}
                  className="w-full p-4 flex items-center justify-between hover:bg-slate-50 text-left">
            <div className="flex items-center gap-3">
              {openGroup === "__ungrouped__" ? <ChevronDown size={18}/> : <ChevronRight size={18}/>}
              <div className="font-display text-lg tracking-tight">Diğer</div>
            </div>
            <span className="text-xs text-slate-500">{tree.ungrouped.length} ana konu</span>
          </button>
          {openGroup === "__ungrouped__" && (
            <div className="border-t border-[#E2E8F0] p-3 space-y-2 bg-slate-50">
              {tree.ungrouped.filter((t) => t.is_active !== false).map(renderTopic)}
            </div>
          )}
        </div>
      )}
      {tree.groups.length === 0 && tree.ungrouped.length === 0 && (
        <div data-testid="topics-empty-state"
             className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-xl p-6 text-center">
          Bu kapsamda henüz konu eklenmemiş.
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-5">
      {needsOnboarding && <ExamTargetModal onDone={setProfile} />}
      <div>
        <div className="flex items-center gap-2">
          <h1 className="font-display text-3xl tracking-tight text-[#0B1A35]">Konular</h1>
          {examType && (
            <span data-testid="exam-scope-badge"
                  className="px-2.5 py-1 rounded-full bg-[#0B1A35] text-[#FFC107] text-xs font-mono">
              {examType}
            </span>
          )}
        </div>
        <p className="text-slate-500 text-sm">Bir konu seç, 10 sorulu bir oturuma başla.</p>
      </div>

      {examType === "YKS" ? (
        <Tabs value={tab} onValueChange={setTab} data-testid="yks-exam-tabs">
          <TabsList className="bg-slate-100 w-full sm:w-auto">
            <TabsTrigger value="TYT" data-testid="exam-tab-tyt" className="flex-1 sm:flex-none px-6">TYT</TabsTrigger>
            <TabsTrigger value="AYT" data-testid="exam-tab-ayt" className="flex-1 sm:flex-none px-6">AYT</TabsTrigger>
          </TabsList>
          <TabsContent value="TYT">{tab === "TYT" && groupList}</TabsContent>
          <TabsContent value="AYT">{tab === "AYT" && groupList}</TabsContent>
        </Tabs>
      ) : groupList}
    </div>
  );
}

function ExamTargetModal({ onDone }) {
  const [target, setTarget] = useState(null);
  const [grade, setGrade] = useState(null);
  const [busy, setBusy] = useState(false);
  const needsGrade = target === "TYT" || target === "YKS";
  const canSubmit = target === "LGS" || (needsGrade && !!grade);

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const payload = { exam_type: target };
      if (needsGrade) payload.education_status = grade;
      const { data } = await api.patch("/me/profile", payload);
      toast.success("Sınav hedefin kaydedildi!");
      onDone(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="exam-target-modal"
         className="fixed inset-0 z-50 bg-[#0B1A35]/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl p-6 sm:p-8 shadow-2xl">
        <div className="flex items-center gap-3 mb-2">
          <img src="/brand/yz-logo.png" alt="" className="h-10 w-10 rounded-full ring-2 ring-[#FFC107]/70" />
          <h2 className="font-display text-2xl tracking-tight text-[#0B1A35]">Hangi sınava hazırlanıyorsun?</h2>
        </div>
        <p className="text-sm text-slate-500 mt-2">
          Konular ve sorular hedefine göre kişiselleşir. Bunu daha sonra profilinden
          değiştirebilirsin — geçmiş çalışmaların silinmez.
        </p>
        <div className="grid grid-cols-3 gap-3 my-6">
          {["LGS", "TYT", "YKS"].map((t) => (
            <button key={t} onClick={() => setTarget(t)}
                    data-testid={`onboarding-target-${t.toLowerCase()}`}
                    className={`p-4 rounded-xl border-2 font-display text-xl tracking-tight transition-colors ${
                      target === t ? "yz-chip-active" : "border-[#E2E8F0] hover:border-[#0B1A35]"
                    }`}>{t}</button>
          ))}
        </div>
        {needsGrade && (
          <div className="mb-6">
            <div className="text-sm font-medium mb-2">Sınıfın</div>
            <div className="flex flex-wrap gap-2">
              {GRADES.map((g) => (
                <button key={g.v} onClick={() => setGrade(g.v)}
                        data-testid={`onboarding-grade-${g.v.toLowerCase()}`}
                        className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                          grade === g.v ? "yz-chip-active" : "border-[#E2E8F0] hover:border-[#0B1A35]"
                        }`}>{g.l}</button>
              ))}
            </div>
          </div>
        )}
        <button onClick={submit} disabled={!canSubmit || busy} data-testid="onboarding-submit-btn"
                className="yz-cta w-full py-2.5 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed">
          {busy ? "Kaydediliyor…" : "Devam Et"}
        </button>
      </div>
    </div>
  );
}
